import React, {FC, useEffect} from 'react';
import {Modal} from 'react-bootstrap';
import DynamicAsset from 'components/DynamicAsset';
import {useToggle} from 'hooks/useToggle';
import {cookies, CookKey} from 'utils/cookies';
import {hasWindow} from 'utils/functions';
import {hsForm} from 'utils/window/hubspot';

import CloseIcon from './close.svg';

import * as styles from './styles.module.scss';

type LeadCaptureProps = {
	formId?: string
	imageSrc?: string
	mobileSrc?: string
	showKey?: string
	onlyShowIf?: OnlyShow
}

enum OnlyShow {
	new = 'new',
	returning = 'returning',
}

const prevSeen = (key: string): boolean => {
	return Boolean(key && cookies.get(CookKey.LeadCapture) === key);
};

const showOnlyIf = (onlyShowIf: OnlyShow | undefined): boolean => {
	if (onlyShowIf === undefined) {
		return true;
	} else if (onlyShowIf === OnlyShow.returning && cookies.get(CookKey.BodySpecUser)) {
		return true;
	} else if (onlyShowIf === OnlyShow.new && !cookies.get(CookKey.BodySpecUser)) {
		return true;
	}

	return false;
};

const LeadCapture: FC<LeadCaptureProps> = ({formId = '', imageSrc, mobileSrc, showKey: key, onlyShowIf}) => {
	const {open, toggle} = useToggle(false);
	const target = 'hs' + formId.substring(0, 8);

	// Calculate our open state & form init in a useEffect
	useEffect(() => {
		if (!key || !formId) {
			return;
		} else if (
			!hasWindow() ||
			prevSeen(key) ||
			!showOnlyIf(onlyShowIf)
		) {
			return;
		}

		// Always set our "seen" cookie
		cookies.set(CookKey.LeadCapture, key, {expires: 365});
		if (!open) toggle();

		// Initialize hubspot form on LC form
		const checkEvery = 100;
		const checkUntil = 5000;

		const check = (left: number) => () => {
			if (hsForm.ready()) {
				if (formId) {
					hsForm.init(formId, target);
				}
			} else if (left > 0) {
				setTimeout(check(left - 1), checkEvery);
			}
		};
		check(checkUntil / checkEvery)();
	}, []);

	return !open ? null : (
		<Modal
			show
			scrollable
			size='lg'
			contentClassName={styles.wrapper}
			onHide={toggle}
		>
			<div className={styles.form} id={target} />
			<div className={styles.image}>
				{mobileSrc && (
					<DynamicAsset
						className={styles.imgMobile}
						imageSrc={mobileSrc}
					/>)}
				{imageSrc && (
					<DynamicAsset
						className={styles.imgDesktop}
						imageSrc={imageSrc}
					/>)}
				<img
					className={styles.close}
					src={CloseIcon as string}
					onClick={toggle}
				/>
			</div>
		</Modal>
	);
};

export default LeadCapture;
